import { Events } from "ionic-angular";
import { NavBarServiceProvider } from "./nav-bar-service";

/**
 * NOVA-1645 — shared "URL :id → re-fetch this section" subscription.
 *
 * Background: Ionic 3 reuses a page instance across a hash `:id` change
 * (manual address-bar edit, popstate). The page binds patient data once
 * (constructor / ionViewDidLoad / ngOnInit) keyed on a captured id and does
 * NOT re-fetch when the URL id changes within the reused instance, so the
 * section content stays on the previous patient while the brief bar updates.
 *
 * NOVA-1610 fixed this for the patient profile page (patient-details.ts) by
 * subscribing to the global `patientChartIdChange` event, re-reading the URL
 * id, and re-fetching when it differs. This helper extracts that pattern so
 * the ~13 non-profile section pages don't each hand-roll (and mis-roll) the
 * subscribe / compare / specific-handler-unsubscribe dance — the last of
 * which is the listener-leak footgun NOVA-1651 / NOVA-1652 had to clean up.
 *
 * The companion half of the fix lives in app.component.ts `handleUrlChange`,
 * which is what PUBLISHES `patientChartIdChange` for these section routes.
 * Without that publisher extension this subscription never fires.
 *
 * Usage (in a page):
 *
 *   private patientCtxSub: PatientChartIdChangeSubscription;
 *
 *   ionViewWillEnter() {
 *     if (!this.patientCtxSub) {
 *       this.patientCtxSub = subscribeToPatientChartIdChange(
 *         this.events,
 *         this.navBarService,
 *         {
 *           getBoundId: () => this.patChartId,        // current bound id
 *           onChange: (urlId) => {                    // sync + re-fetch
 *             this.patChartId = String(urlId);
 *             this.loadData();
 *           },
 *         }
 *       );
 *     }
 *   }
 *
 *   ionViewDidLeave() {
 *     if (this.patientCtxSub) {
 *       this.patientCtxSub.unsubscribe();
 *       this.patientCtxSub = undefined;
 *     }
 *   }
 *
 * Invariants carried over from NOVA-1610:
 *  - Read the URL id via navBarService.parsePatientChartIdsFromUrl — do NOT
 *    trust data.id / data.externalId from the event payload (the popstate
 *    handler's payload mislabels the URL id as externalId for legacy patients
 *    without the < 199999 heuristic).
 *  - Bail when the URL id equals the currently-bound id (idempotency — the
 *    event fires on same-patient re-entry / back+hashchange double-fire too).
 *  - Unsubscribe the SPECIFIC handler reference; Events.unsubscribe(topic)
 *    with no handler clears ALL listeners on that topic (incl. brief bar's).
 *  - Subscribe in ionViewWillEnter (re-subscribe on return from a sibling
 *    tab), unsubscribe in ionViewDidLeave.
 */
export interface PatientChartIdChangeOptions {
  /**
   * Returns the patient chart id the section is currently bound to. The URL id
   * resolved by parsePatientChartIdsFromUrl is the first numeric segment of
   * the hash, which for every patient section route is the internal patient
   * chart id — so compare against the page's internal id field.
   */
  getBoundId: () => number | string;

  /**
   * Invoked only when the URL id differs from the bound id. Receives the new
   * URL patient chart id. The page should sync its id field(s) and call its
   * data-fetch method here.
   */
  onChange: (urlId: number) => void;
}

export interface PatientChartIdChangeSubscription {
  unsubscribe: () => void;
}

export function subscribeToPatientChartIdChange(
  events: Events,
  navBarService: NavBarServiceProvider,
  options: PatientChartIdChangeOptions
): PatientChartIdChangeSubscription {
  const handler = (data: any) => {
    if (!data || !data.id) {
      return;
    }
    const urlIds = navBarService.parsePatientChartIdsFromUrl(
      window.location.hash
    );
    const urlId = urlIds && urlIds.patientChartId;
    if (!urlId) {
      return;
    }
    const boundId = options.getBoundId();
    if (boundId != null && Number(urlId) === Number(boundId)) {
      return;
    }
    options.onChange(urlId);
  };

  events.subscribe("patientChartIdChange", handler);

  return {
    unsubscribe: () => {
      events.unsubscribe("patientChartIdChange", handler);
    },
  };
}



// WEBPACK FOOTER //
// ./src/providers/nav-bar-service/patient-chart-id-change.ts